import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { InventoryEntities } from 'client/data/models/inventory';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { SCROLL_DURATION } from 'client/site-modules/shared/constants/sub-navigation';
import { transformInventoryComments } from 'site-modules/shared/utils/inventory/seller-comment-utils';

import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';

import './inventory-comments.scss';

const MAX_CONTAINER_HEIGHT = 66; // 3 lines of text, height of one line of text is 22px
const SCROLL_OFFSET = -110;

export const InventoryComments = ({
  vinData = {},
  fieldName = 'sellersComments',
  sectionClassName = 'inventory-comments',
  sectionTitle = 'Dealer Description',
  dataTrackingParent = 'edm-entry-dealer-description',
  elementName = 'scrollTop-more-about',
  line = true,
}) => {
  const [dynamic, setDynamic] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const height = containerRef?.current?.container?.offsetHeight ?? 0;
    const isContainerDynamic = height > MAX_CONTAINER_HEIGHT;

    if (dynamic !== isContainerDynamic) {
      setDynamic(isContainerDynamic);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const text = transformInventoryComments(vinData?.[fieldName] ?? '');

  if (!text) {
    return null;
  }

  const commentBlock = (
    <ContentFragment ref={containerRef} classes={classNames('mb-0_5')}>
      {text}
    </ContentFragment>
  );

  return (
    <ContentSection
      className={sectionClassName}
      sectionTitle={sectionTitle}
      dataTrackingParent={dataTrackingParent}
      titleClassName="section-title heading-4 mb-1_5"
      line={line}
    >
      {dynamic ? (
        <ContentCollapse
          textClosed="Read more"
          textOpen="Read less"
          collapseToggleSrExplanation={`about ${sectionTitle}`}
          justify="justify-content-start"
          btnContainerClasses="mt-0_5 pt-0 pb-0_5"
          btnClasses="px-0_5 py-0 size-16 text-primary-darker more-info-btn"
          btnTextClasses="fw-normal"
          minHeight={`${MAX_CONTAINER_HEIGHT}px`}
          showBtnBelow
          isEclipseFade={false}
          resetParams={{
            offset: SCROLL_OFFSET,
            duration: SCROLL_DURATION,
            to: elementName,
          }}
          btnTrackingId="vdp_view_content"
        >
          <ScrollElement id={elementName} />
          {commentBlock}
        </ContentCollapse>
      ) : (
        commentBlock
      )}
    </ContentSection>
  );
};

InventoryComments.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  fieldName: PropTypes.string,
  sectionClassName: PropTypes.string,
  sectionTitle: PropTypes.string,
  dataTrackingParent: PropTypes.string,
  elementName: PropTypes.string,
  line: PropTypes.bool,
};
